/**
 * Spec 2026-08-25 — eenmalige backfill: elke levende maat-variant krijgt een
 * voorraadartikel. Bestaande kleding/PBM-rijen worden gematcht op naam-overlap
 * (>= 0,66) + maat (maatMatcht); precies één kandidaat -> koppelen; geen
 * kandidaat -> nieuw artikel; meerdere -> TWIJFEL (handmatig via de UI).
 *
 * Pure kern van het eenmalige backfill-script
 * (`packages/db/prisma/scripts/koppel-uitrusting-varianten-voorraad.ts`) —
 * hier ondergebracht (i.p.v. in het db-script zelf) zodat de tests meedraaien
 * in de deploy-gate: utils-tests draaien mee, db-scripts niet. Het db-script
 * is een dunne CLI eromheen (argumenten, JSON inlezen, rapport printen).
 */
import {
  artikelnaamJaccard,
  artikelnaamOverlap,
  normaliseerArtikelnaamVoorMatch,
  voorraadCategorieVoorUitrusting,
  voorraadMaatVoorVariant,
} from './uitrusting-variant-voorraad'
import { isMaatloos, maatMatcht } from './uitrusting-maat'

/** Eén levende maat-variant uit de `varianten.json`-export (zie docblock CLI-script). */
export interface VariantRij {
  id: string
  artikelnummer: string
  maat: string | null
  maatType: string | null
  kleur: string | null
  prijsExclBtw: string
  actief: boolean
  voorraadArtikelId: string | null
  artikelId: string
  artikelNaam: string
  categorie: 'KLEDING' | 'GEREEDSCHAP' | 'PBM' | 'VEILIGHEID'
  leverancierNaam: string | null
}

/** Eén kandidaat-voorraadrij (kleding/PBM) uit de `voorraad.json`-export. */
export interface VoorraadRij {
  id: string
  artikelNummer: string
  naam: string
  maat: string | null
  kleur: string | null
  categorie: string
  saldo: number
  alGekoppeld: boolean
}

/** Besluit per variant: al gekoppeld, koppelen aan een bestaande bron, nieuw aanmaken, of handmatig beoordelen. */
export type Besluit =
  | { soort: 'BESTAAT'; variant: VariantRij }
  | { soort: 'KOPPEL'; variant: VariantRij; bron: VoorraadRij }
  | { soort: 'NIEUW'; variant: VariantRij }
  | { soort: 'TWIJFEL'; variant: VariantRij; kandidaten: VoorraadRij[] }

/**
 * Maat-vergelijking tussen een variant en een voorraadkandidaat: maatloos
 * matcht uitsluitend tegen maatloos (nooit tegen een concrete maat, en
 * andersom), anders via de gedeelde `maatMatcht`.
 */
function maatPast(v: VariantRij, b: VoorraadRij): boolean {
  const vLoos = v.maat == null || isMaatloos(v.maat)
  const bLoos = b.maat == null || isMaatloos(b.maat)
  if (vLoos || bLoos) return vLoos && bLoos
  return maatMatcht(b.maat, v.maat, v.maatType)
}

/**
 * Een voorraadnaam van minder dan twee tokens ("Werkbroek", "Winterjas",
 * "Veiligheidsbril", "Otoplastieken") zegt niets over merk of model en kan bij
 * meerdere catalogusartikelen horen. Zo'n kandidaat koppelen we nooit
 * automatisch: de eerste variant zou 'm anders claimen en de rest niets krijgen.
 */
function isGeneriekeVoorraadnaam(b: VoorraadRij): boolean {
  return normaliseerArtikelnaamVoorMatch(b.naam).length < 2
}

/** Jaccard-score van een kandidaat t.o.v. de catalogusnaam (0..1). */
export function kandidaatScore(v: VariantRij, b: VoorraadRij): number {
  return artikelnaamJaccard(v.artikelNaam, b.naam)
}

/**
 * Bepaalt per variant het besluit. Een reeds gekoppelde variant blijft
 * BESTAAT (geen wijziging). Anders zoekt de functie kandidaten op
 * naam-overlap (>= 0,66) + maat. Geen kandidaat -> NIEUW. Meer dan één ->
 * de hoogste JACCARD-score wint (die straft niet-gedeelde tokens af, dus
 * "Lebec" wint van "Ledburg"); een gelijkspel op de hoogste score, of een
 * generieke winnaar-naam, is TWIJFEL (handmatig via de UI). Een voorraadbron
 * wordt maar één keer geclaimd, ook binnen dezelfde run (volgorde van
 * `varianten` bepaalt wie een gedeelde bron krijgt).
 */
export function bepaalBesluiten(varianten: VariantRij[], voorraad: VoorraadRij[]): Besluit[] {
  const geclaimd = new Set(voorraad.filter((b) => b.alGekoppeld).map((b) => b.id))
  const besluiten: Besluit[] = []
  for (const v of varianten) {
    if (v.voorraadArtikelId) {
      besluiten.push({ soort: 'BESTAAT', variant: v })
      continue
    }
    const kandidaten = voorraad.filter(
      (b) => !geclaimd.has(b.id) && artikelnaamOverlap(v.artikelNaam, b.naam) >= 0.66 && maatPast(v, b),
    )
    if (kandidaten.length === 0) {
      besluiten.push({ soort: 'NIEUW', variant: v })
      continue
    }
    const hoogste = Math.max(...kandidaten.map((b) => kandidaatScore(v, b)))
    // Floating point: scores zijn breuken, dus vergelijk met een marge.
    const winnaars = kandidaten.filter((b) => Math.abs(kandidaatScore(v, b) - hoogste) < 1e-9)
    const winnaar = winnaars.length === 1 ? winnaars[0]! : null
    if (winnaar && !isGeneriekeVoorraadnaam(winnaar)) {
      geclaimd.add(winnaar.id)
      besluiten.push({ soort: 'KOPPEL', variant: v, bron: winnaar })
    } else {
      besluiten.push({ soort: 'TWIJFEL', variant: v, kandidaten })
    }
  }
  return besluiten
}

/** SQL-string-literal, of `NULL`; verdubbelt enkele quotes. */
function sqlStr(s: string | null): string {
  return s == null ? 'NULL' : `'${s.replace(/'/g, "''")}'`
}

/**
 * Genereert een uniek id voor een gegenereerde SQL-rij. Structureel getypeerd
 * op `globalThis.crypto` (GEEN `node:crypto`-import) zodat dit bestand, dat via
 * de barrel ook door de frontends geladen wordt, in hun bundels blijft
 * compileren — zelfde patroon als `newIdempotencyKey` in `offline-sync.ts`.
 * `crypto.randomUUID` is beschikbaar in Node 20+ en alle doel-browsers.
 */
function nieuwId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0')
  return `id-${Date.now().toString(16)}-${rnd()}-${rnd()}`
}

/**
 * QR-token voor een gegenereerd voorraadartikel: 16 random bytes als base64url
 * (22 tekens), exact de vorm van `genereerQrToken` in de api (`lib/qr.ts`), zodat
 * ook de backfill-artikelen scanbaar zijn. Structureel getypeerd op
 * `globalThis.crypto` (GEEN `node:crypto`-import), zelfde reden als `nieuwId`.
 */
function nieuwQrToken(): string {
  const bytes = new Uint8Array(16)
  const c = (globalThis as {
    crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array }
  }).crypto
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  let binair = ''
  for (const b of bytes) binair += String.fromCharCode(b)
  return btoa(binair).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** `prijsExclBtw` komt als `::text` uit de export; alleen een kaal decimaal literal is veilig om ongequote te interpoleren. */
const DECIMAAL_PATROON = /^\d+(\.\d+)?$/

/**
 * Guard: een corrupte export (geen kaal decimaal literal) mag nooit
 * ongevalideerd de SQL in - dan liever hard falen met de variant-id.
 */
function veiligePrijs(v: VariantRij): string {
  if (!DECIMAAL_PATROON.test(v.prijsExclBtw)) {
    throw new Error(
      `Ongeldige prijsExclBtw "${v.prijsExclBtw}" voor variant ${v.id} - corrupte export?`,
    )
  }
  return v.prijsExclBtw
}

/**
 * Bouwt de SQL voor alle KOPPEL/NIEUW-besluiten als één transactie
 * (BEGIN/COMMIT). Nieuwe artikelnummers lopen door vanaf `maxArt`
 * (ART-<maxArt+1>, ...). Elke wijziging krijgt een koppel-log-rij
 * (`uitrusting_artikel_koppel_logs`, `createdAt` heeft een DB-default, geen
 * `updatedAt`). BESTAAT/TWIJFEL genereren geen SQL.
 */
export function bouwSql(besluiten: Besluit[], maxArt: number, actorUserId: string | null): string {
  let volgnr = maxArt
  const regels: string[] = ['BEGIN;']
  for (const b of besluiten) {
    if (b.soort === 'KOPPEL') {
      const maat = voorraadMaatVoorVariant(b.variant.maat, b.variant.maatType)
      const prijs = veiligePrijs(b.variant)
      regels.push(
        `UPDATE uitrusting_varianten SET "voorraadArtikelId" = ${sqlStr(b.bron.id)} WHERE id = ${sqlStr(b.variant.id)};`,
        // Uit voorraad geleverde kleding boekt op de VOORRAADprijs: een lege
        // inkoopPrijs (32 prod-rijen) vullen we eenmalig met de catalogusprijs;
        // een bestaande prijs blijft staan (COALESCE).
        `UPDATE voorraad_artikelen SET maat = ${sqlStr(maat)}, "inkoopPrijs" = COALESCE("inkoopPrijs", ${prijs}) WHERE id = ${sqlStr(b.bron.id)};`,
        `INSERT INTO uitrusting_artikel_koppel_logs (id, "artikelId", "variantId", "voorraadArtikelId", actie, "actorUserId") VALUES (${sqlStr(nieuwId())}, ${sqlStr(b.variant.artikelId)}, ${sqlStr(b.variant.id)}, ${sqlStr(b.bron.id)}, 'VARIANT_GEKOPPELD', ${sqlStr(actorUserId)});`,
      )
    } else if (b.soort === 'NIEUW') {
      const prijs = veiligePrijs(b.variant)
      volgnr += 1
      const id = nieuwId()
      const nummer = `ART-${String(volgnr).padStart(4, '0')}`
      const maat = voorraadMaatVoorVariant(b.variant.maat, b.variant.maatType)
      const actief = b.variant.actief === true ? 'true' : 'false'
      regels.push(
        `INSERT INTO voorraad_artikelen (id, "artikelNummer", naam, categorie, eenheid, maat, kleur, leverancier, "leverancierArtNr", "inkoopPrijs", "minimumVoorraad", actief, traceerbaar, bestelbaar, "qrToken", "createdAt", "updatedAt") VALUES (${sqlStr(id)}, ${sqlStr(nummer)}, ${sqlStr(b.variant.artikelNaam)}, ${sqlStr(voorraadCategorieVoorUitrusting(b.variant.categorie))}, 'STUK', ${sqlStr(maat)}, ${sqlStr(b.variant.kleur)}, ${sqlStr(b.variant.leverancierNaam)}, ${sqlStr(b.variant.artikelnummer)}, ${prijs}, 0, ${actief}, false, false, ${sqlStr(nieuwQrToken())}, now(), now());`,
        `UPDATE uitrusting_varianten SET "voorraadArtikelId" = ${sqlStr(id)} WHERE id = ${sqlStr(b.variant.id)};`,
        `INSERT INTO uitrusting_artikel_koppel_logs (id, "artikelId", "variantId", "voorraadArtikelId", actie, "actorUserId") VALUES (${sqlStr(nieuwId())}, ${sqlStr(b.variant.artikelId)}, ${sqlStr(b.variant.id)}, ${sqlStr(id)}, 'VARIANT_AANGEMAAKT', ${sqlStr(actorUserId)});`,
      )
    }
  }
  regels.push('COMMIT;')
  return regels.join('\n')
}

/** Jaccard-score op twee decimalen, voor het dry-run-rapport. */
function score(v: VariantRij, b: VoorraadRij): string {
  return kandidaatScore(v, b).toFixed(2)
}

/**
 * Mensleesbaar dry-run-rapport, gegroepeerd per artikel: een kop met de
 * totalen (koppelen/nieuw/twijfel/al gekoppeld), per artikel een regel per maat
 * met de uitkomst (bij KOPPEL en TWIJFEL mét de Jaccard-score per kandidaat), en
 * onderaan de voorraadrijen die bij geen enkele variant horen. Die weeslijst is
 * bewust zichtbaar: die rijen blijven staan en verklaren waarom een deel van de
 * voorraad na de backfill niet bestelbaar is (spec §10).
 */
export function rapport(besluiten: Besluit[], voorraad: VoorraadRij[]): string {
  const telling = { BESTAAT: 0, KOPPEL: 0, NIEUW: 0, TWIJFEL: 0 }
  const perArtikel = new Map<string, string[]>()
  const geclaimd = new Set<string>()
  for (const b of besluiten) {
    telling[b.soort] += 1
    if (b.soort === 'KOPPEL') geclaimd.add(b.bron.id)
    const regel =
      b.soort === 'KOPPEL' ? `${b.variant.maat ?? '-'} -> ${b.bron.artikelNummer} (${b.bron.naam}, saldo ${b.bron.saldo}, score ${score(b.variant, b.bron)})`
      : b.soort === 'NIEUW' ? `${b.variant.maat ?? '-'} -> NIEUW`
      : b.soort === 'TWIJFEL' ? `${b.variant.maat ?? '-'} -> TWIJFEL: ${b.kandidaten.map((k) => `${k.artikelNummer} (${k.naam} ${k.maat ?? ''}, score ${score(b.variant, k)})`).join(' | ')}`
      : `${b.variant.maat ?? '-'} -> al gekoppeld`
    const lijst = perArtikel.get(b.variant.artikelNaam) ?? []
    lijst.push(regel)
    perArtikel.set(b.variant.artikelNaam, lijst)
  }
  const wezen = voorraad.filter((b) => !b.alGekoppeld && !geclaimd.has(b.id))
  const kop = `Backfill variant-voorraad: ${telling.KOPPEL} koppelen, ${telling.NIEUW} nieuw, ${telling.TWIJFEL} twijfel, ${telling.BESTAAT} al gekoppeld\n`
  const body = [...perArtikel.entries()]
    .map(([naam, regels]) => `\n${naam}\n  ${regels.join('\n  ')}`)
    .join('\n')
  const staart =
    `\n\nVoorraadrijen zonder catalogus-tegenhanger (blijven staan): ${wezen.length}` +
    wezen.map((w) => `\n  ${w.artikelNummer} · ${w.naam} · ${w.maat ?? '-'} · saldo ${w.saldo}`).join('')
  return kop + body + staart
}
